import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function ContentFutureSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setAnimationStep((prev) => (prev + 1) % 4);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const contentFlow = [
    {
      step: "AI Generation",
      icon: "ri-robot-line",
      description: "Intelligent content creation",
      color: "from-purple-500 to-pink-500"
    },
    {
      step: "Content Optimization", 
      icon: "ri-settings-3-line",
      description: "Smart refinement & targeting",
      color: "from-pink-500 to-red-500"
    },
    {
      step: "Multi-Platform Distribution",
      icon: "ri-share-line", 
      description: "Automated publishing",
      color: "from-red-500 to-orange-500"
    },
    {
      step: "Performance Analytics",
      icon: "ri-line-chart-line",
      description: "Real-time insights",
      color: "from-orange-500 to-yellow-500"
    }
  ];

  const platforms = [
    { name: "Social Media", icon: "ri-instagram-line", color: "text-pink-400" },
    { name: "Email", icon: "ri-mail-line", color: "text-blue-400" },
    { name: "Website", icon: "ri-global-line", color: "text-green-400" },
    { name: "Blog", icon: "ri-article-line", color: "text-purple-400" },
    { name: "Video", icon: "ri-video-line", color: "text-red-400" },
    { name: "Podcast", icon: "ri-mic-line", color: "text-yellow-400" }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-black"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            The Future of Content:
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Intelligent Creation & Distribution
            </span>
          </h2>
          
          <h3 className="text-xl md:text-3xl font-semibold text-orange-400 mb-6">
            Why AI Automated Content Generation over Manual Content Generation for the AI Era
          </h3>

          <div className="max-w-5xl mx-auto">
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              In a world saturated with information, relying solely on manual content creation and publishing 
              is no longer sustainable. We help you embrace the power of AI to generate engaging content 
              efficiently and automate its timely distribution across relevant channels.
            </p>
          </div>
        </div>

        <div className={`mb-16 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="relative bg-gray-800/30 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-500/20 overflow-hidden">
            <h4 className="text-2xl font-bold text-white text-center mb-12">
              Automated Content Workflow
            </h4>
            
            <div className="relative overflow-x-auto pb-8 md:pb-0">
              <div className="flex items-start justify-between min-w-[800px] mx-auto px-4 md:px-12 pt-4">
                {contentFlow.map((item, index) => (
                  <div key={index} className="flex flex-col items-center relative flex-1">
                    <div className={`relative w-20 h-20 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center transition-all duration-500 z-10 ${
                      animationStep >= index ? 'scale-110 shadow-lg shadow-orange-500/20' : 'scale-100 grayscale opacity-70'
                    }`}>
                      <i className={`${item.icon} text-2xl text-white`}></i>
                      
                      {animationStep === index && (
                        <div className="absolute inset-0 rounded-full border-2 border-white animate-ping"></div>
                      )}
                    </div>
                    
                    <div className="text-center mt-6">
                      <h5 className="text-white font-semibold text-sm mb-1">{item.step}</h5>
                      <p className="text-gray-400 text-xs px-2">{item.description}</p>
                    </div>
                    
                    {index < contentFlow.length - 1 && (
                      <div className="absolute top-10 left-[50%] right-[-50%] h-1 bg-gray-700 -z-0">
                        <div className={`h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500 ${
                          animationStep > index ? 'w-full' : 'w-0'
                        }`}></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={`transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="text-center mb-8">
            <h4 className="text-2xl font-bold text-white mb-4">
              Distribute Across All Channels
            </h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className={`group bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-orange-500/20 hover:border-orange-500/50 transition-all duration-500 hover:transform hover:scale-110`}
              >
                <div className="text-center">
                  <i className={`${platform.icon} text-3xl ${platform.color} mb-3 inline-block group-hover:animate-bounce`}></i>
                  <h5 className="text-white font-semibold text-sm">{platform.name}</h5>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`text-center mt-16 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <Link to="/contact" className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer inline-block">
            <span className="flex items-center gap-3">
              Start Content Automation
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}